import { ArrowDown, ArrowRight, Boxes, Cloud, Code2, Database, FileJson, KeyRound, Monitor, Server, Workflow } from 'lucide-react';
import type { ElementType, ReactNode } from 'react';

import { cn } from '@/lib/utils';

import type { DiagramKind } from './handbookTypes';
import { useHandbook } from './HandbookContext';

function Node({ icon: Icon, title, detail, accent, className }: { icon?: ElementType; title: string; detail?: string; accent?: boolean; className?: string }) {
  return (
    <div
      className={cn(
        'flex min-w-0 items-start gap-2.5 rounded-lg border px-3 py-2.5',
        accent ? 'border-brand/40 bg-brand-muted/40' : 'border-border bg-card',
        className,
      )}
    >
      {Icon ? <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', accent ? 'text-brand' : 'text-muted-foreground')} /> : null}
      <div className="min-w-0">
        <div className="text-xs font-semibold text-foreground">{title}</div>
        {detail ? <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{detail}</div> : null}
      </div>
    </div>
  );
}

function Down({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
      <ArrowDown className="h-3.5 w-3.5" />
      {label}
    </div>
  );
}

function Chip({ children, accent, muted }: { children: ReactNode; accent?: boolean; muted?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium',
        accent ? 'border-brand/40 bg-brand-muted text-foreground' : muted ? 'border-dashed border-border text-muted-foreground' : 'border-border bg-card',
      )}
    >
      {children}
    </span>
  );
}

function Arrow({ label }: { label?: string }) {
  return (
    <span className="inline-flex flex-col items-center px-1 text-muted-foreground">
      {label ? <span className="mb-0.5 whitespace-nowrap text-[10px]">{label}</span> : null}
      <ArrowRight className="h-3.5 w-3.5" />
    </span>
  );
}

function Architecture({text}: {text: Record<string, string>}) {
  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_200px]">
      <div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Node icon={Monitor} title={text['d001']} detail={text['d002']} />
          <Node icon={Monitor} title={text['d003']} detail={text['d004']} />
        </div>
        <Down label={text['d005']} />
        <Node icon={Server} title={text['d006']} detail={text['d007']} />
        <Down label={text['d008']} />
        <Node icon={Workflow} title={text['d009']} detail={text['d010']} />
        <Down label={text['d011']} />
        <div className="rounded-xl border border-brand/40 bg-brand-muted/20 p-3">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-brand">
            <Boxes className="h-3.5 w-3.5" />{text['d012']}</div>
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <Node title={text['d013']} detail={text['d014']} />
            <Node title={text['d015']} detail={text['d016']} />
            <Node title={text['d017']} detail={text['d018']} />
            <Node title={text['d019']} detail={text['d020']} />
            <Node title={text['d021']} detail={text['d022']} />
          </div>
        </div>
        <Down label={text['d023']} />
        <div className="rounded-xl border border-dashed border-border p-3">
          <Node icon={Cloud} title={text['d024']} detail={text['d025']} />
        </div>
      </div>
      <div className="flex flex-col gap-2 border-t border-dashed border-border pt-4 md:border-l md:border-t-0 md:pl-4 md:pt-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{text['d026']}</div>
        <Node icon={Workflow} title={text['d027']} detail={text['d028']} />
        <Node icon={Server} title={text['d029']} detail={text['d030']} />
        <Node icon={Database} title={text['d031']} detail={text['d032']} />
        <Node icon={Database} title={text['d033']} detail={text['d034']} />
        <Node icon={Database} title={text['d035']} detail={text['d036']} />
        <Node icon={KeyRound} title={text['d037']} detail={text['d038']} />
      </div>
    </div>
  );
}

function Lifecycle({text}: {text: Record<string, string>}) {
  const steps = [text['d039'], text['d040'], text['d041'], text['d042'], text['d043'], text['d044'], text['d045']];
  const background = ['Checks', 'Thumbnail', 'Background repair'];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-y-3">
        {steps.map((step, index) => (
          <span key={step} className="inline-flex items-center">
            <Chip accent={step === 'Agent turn' || step === 'Preview ready'}>
              <span className="mr-1.5 font-mono text-[10px] text-muted-foreground">{index + 1}</span>
              {step}
            </Chip>
            {index < steps.length - 1 ? <Arrow /> : null}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-y-3">
        <span className="mr-2 text-[10px] uppercase tracking-wider text-muted-foreground">{text['d046']}</span>
        {background.map((step, index) => (
          <span key={step} className="inline-flex items-center">
            <Chip muted>{step}</Chip>
            {index < background.length - 1 ? <Arrow /> : null}
          </span>
        ))}
      </div>
    </div>
  );
}

function MicrovmStates({text}: {text: Record<string, string>}) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-y-3">
        <Chip muted>{text['d047']}</Chip>
        <Arrow label={text['d048']} />
        <Chip accent>{text['d049']}</Chip>
        <Arrow label={text['d050']} />
        <Chip>{text['d051']}</Chip>
        <Arrow label={text['d052']} />
        <Chip>{text['d053']}</Chip>
      </div>
      <div className="grid gap-2 text-[11px] text-muted-foreground sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card px-3 py-2">
          <span className="font-medium text-foreground">{text['d054']}</span>{text['d055']}<code className="font-mono">{text['d056']}</code>{text['d057']}</div>
        <div className="rounded-lg border border-border bg-card px-3 py-2">
          <span className="font-medium text-foreground">{text['d058']}</span>{text['d059']}<code className="font-mono">{text['d060']}</code>{text['d061']}</div>
        <div className="rounded-lg border border-border bg-card px-3 py-2">
          <span className="font-medium text-foreground">{text['d062']}</span>{text['d063']}</div>
      </div>
    </div>
  );
}

function CredentialFlow({text}: {text: Record<string, string>}) {
  return (
    <div className="grid items-center gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1.2fr]">
      <div className="space-y-2">
        <Node icon={KeyRound} title={text['d064']} detail={text['d065']} />
        <Node icon={KeyRound} title={text['d066']} detail={text['d067']} />
      </div>
      <ArrowRight className="mx-auto hidden h-4 w-4 text-muted-foreground lg:block" />
      <Node title={text['d068']} detail={text['d069']} accent />
      <ArrowRight className="mx-auto hidden h-4 w-4 text-muted-foreground lg:block" />
      <Node title={text['d070']} detail={text['d071']} />
      <ArrowRight className="mx-auto hidden h-4 w-4 text-muted-foreground lg:block" />
      <div className="space-y-2">
        <Node icon={Monitor} title={text['d072']} detail={text['d073']} />
        <Node icon={Server} title={text['d074']} detail={text['d075']} />
      </div>
    </div>
  );
}

function IntegrationRoutes({text}: {text: Record<string, string>}) {
  return (
    <div className="grid items-center gap-4 lg:grid-cols-[minmax(0,1fr)_auto_220px]">
      <div className="space-y-3">
        <div className="rounded-xl border border-border bg-card/60 p-3">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <FileJson className="h-3.5 w-3.5" />{text['d076']}</div>
          <div className="flex flex-wrap items-center gap-y-2">
            <Chip>{text['d077']}</Chip>
            <Arrow />
            <Chip>{text['d078']}</Chip>
            <Arrow />
            <Chip>{text['d079']}</Chip>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card/60 p-3">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Code2 className="h-3.5 w-3.5" />{text['d080']}</div>
          <div className="flex flex-wrap items-center gap-y-2">
            <Chip>{text['d081']}</Chip>
            <Arrow />
            <Chip>{text['d082']}</Chip>
            <Arrow />
            <Chip>{text['d083']}</Chip>
          </div>
        </div>
      </div>
      <ArrowRight className="mx-auto hidden h-5 w-5 text-muted-foreground lg:block" />
      <Node
        accent
        title={text['d084']}
        detail={text['d085']}
      />
    </div>
  );
}

function ConnectorRuntime({text}: {text: Record<string, string>}) {
  return (
    <div className="mx-auto max-w-xl">
      <Node icon={Boxes} title={text['d086']} detail={text['d087']} accent />
      <Down label={text['d088']} />
      <div className="rounded-xl border border-border bg-card/40 p-3">
        <Node icon={Server} title={text['d089']} detail={text['d090']} />
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Node icon={KeyRound} title={text['d091']} detail={text['d092']} />
          <Node icon={Database} title={text['d093']} detail={text['d094']} />
        </div>
      </div>
      <Down label={text['d095']} />
      <Node icon={Cloud} title={text['d096']} detail={text['d097']} />
    </div>
  );
}

function MeteringFlow({text}: {text: Record<string, string>}) {
  const steps = [text['d098'], text['d099'], text['d100'], text['d101'], text['d102'], text['d103']];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-y-3">
        {steps.map((step, index) => (
          <span key={step} className="inline-flex items-center">
            <Chip accent={index === 1}>{step}</Chip>
            {index < steps.length - 1 ? <Arrow /> : null}
          </span>
        ))}
      </div>
      <div className="grid gap-2 text-[11px] text-muted-foreground sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-card px-3 py-2">
          <span className="font-medium text-foreground">{text['d104']}</span>{text['d105']}</div>
        <div className="rounded-lg border border-border bg-card px-3 py-2">
          <span className="font-medium text-foreground">{text['d106']}</span>{text['d107']}<code className="font-mono">{text['d108']}</code>{text['d109']}</div>
      </div>
    </div>
  );
}

const DIAGRAMS: Record<DiagramKind, (props: {text: Record<string, string>}) => ReactNode> = {
  architecture: Architecture,
  lifecycle: Lifecycle,
  'microvm-states': MicrovmStates,
  'credential-flow': CredentialFlow,
  'integration-routes': IntegrationRoutes,
  'connector-runtime': ConnectorRuntime,
  'metering-flow': MeteringFlow,
};

export function HandbookDiagram({ kind, caption }: { kind: DiagramKind; caption?: string }) {
  const Diagram = DIAGRAMS[kind];
  const { diagramText } = useHandbook();
  return (
    <figure className="my-8" data-handbook-diagram={kind}>
      <div className="overflow-x-auto rounded-xl border border-border bg-background/60 p-5 [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:16px_16px]">
        <Diagram text={diagramText} />
      </div>
      {caption ? <figcaption className="mt-2 text-center text-xs text-muted-foreground">{caption}</figcaption> : null}
    </figure>
  );
}
